/**
 * "/admin" — the admin-defined `/` script line kinds (F-17-type, 2026-09-04).
 *
 * A kind is a KEYWORD and a STYLE. The keyword is what a writer types after
 * `/` in a cell or a script block; the style is the ink the run is drawn in,
 * and whether it is italic and/or semi-bold. The built-in Sound and Visual
 * kinds are shown first, read-only, so the list reads as the whole menu the
 * writers will see — an admin adding "Shout" sees it land beside them.
 *
 * Every row previews EXACTLY what a cell will show, through the same injected
 * stylesheet the grid uses (features/text-kinds/styles.ts): the row wears the
 * kind's class, not a copy of its style, so a preview can never disagree with
 * a board. The add form previews the same way through a scratch stylesheet
 * on the row itself (inline style — the kind has no id yet).
 *
 * Writes go to the server and then refresh the shared store, so the `/` menu
 * in any board open in this tab offers the new kind at once.
 */
import {
  isTextKindColor,
  isTextKindLabel,
  TEXT_KIND_LABEL_MAX_LENGTH,
  TEXT_KIND_MAX_COUNT,
  type TextKind,
  type TextKindInput,
} from "@kola/shared";
import { useState, type CSSProperties } from "react";

import { ApiError, createTextKind, deleteTextKind, updateTextKind } from "../../core/api.js";
import { Modal } from "../../ui/Modal.js";
import { textTypeClassName } from "../../ui/text-type.js";
import { showToast } from "../../ui/toast.js";
import { loadTextKinds } from "../text-kinds/load.js";
import { useTextKinds } from "../text-kinds/store.js";

/** What the preview row reads — a line a writer might actually mark up. */
const SAMPLE_TEXT = "she turns to the camera";

/**
 * Ink presets — the same eight the text-color palette offers (features/
 * selection-toolbar/palette.ts TEXT_PRESETS are pastel-free inks), so a kind's
 * color and a hand-picked cell color come from one family.
 */
const INK_PRESETS: readonly string[] = [
  "#1d4ed8",
  "#0f766e",
  "#7c3aed",
  "#b45309",
  "#be185d",
  "#374151",
  "#15803d",
  "#b91c1c",
];

/** The user-facing reason for a refused write (the route's error codes). */
function kindErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "duplicate-label":
        return "That keyword is already taken — by another kind, or by a built-in one.";
      case "invalid-label":
        return `A keyword is one word of up to ${TEXT_KIND_LABEL_MAX_LENGTH} characters, with no spaces.`;
      case "invalid-color":
        return "Pick a color as #rrggbb.";
      case "too-many-kinds":
        return `A workspace can have up to ${TEXT_KIND_MAX_COUNT} kinds.`;
      case "forbidden":
        return "Only an admin can change the kinds.";
      case "unknown-kind":
        return "That kind was already removed.";
      default:
        return `The change failed (${error.status}). Try again.`;
    }
  }
  return "The change failed — check your connection and try again.";
}

/** A draft of one kind's editable fields. */
interface KindDraft {
  label: string;
  color: string;
  italic: boolean;
  bold: boolean;
}

function draftOf(kind: TextKind): KindDraft {
  return { label: kind.label, color: kind.color, italic: kind.italic, bold: kind.bold };
}

function sameDraft(a: KindDraft, b: KindDraft): boolean {
  return (
    a.label === b.label &&
    a.color.toLowerCase() === b.color.toLowerCase() &&
    a.italic === b.italic &&
    a.bold === b.bold
  );
}

/**
 * The inline style that paints a draft the way the injected stylesheet would
 * paint the saved kind — used by the add form (no id yet) and by a row whose
 * draft differs from what is saved (so edits preview before Save).
 */
function draftStyle(draft: KindDraft): CSSProperties {
  const style: CSSProperties = {};
  if (isTextKindColor(draft.color)) style.color = draft.color;
  if (draft.bold) style.textShadow = "0.25px 0 0 currentColor, -0.25px 0 0 currentColor";
  if (draft.italic) style.fontStyle = "italic";
  return style;
}

/** `‹ sample ›` (or `[ sample ]` for a built-in), in the kind's style. */
function KindPreview({
  className,
  style,
  brackets,
}: {
  className?: string | undefined;
  style?: CSSProperties | undefined;
  brackets: [string, string];
}) {
  return (
    <span className="kind-preview tt-shown" aria-hidden="true">
      <span className="tt-bracket">{brackets[0]}</span>
      <span className={className} style={style}>
        {SAMPLE_TEXT}
      </span>
      <span className="tt-bracket">{brackets[1]}</span>
    </span>
  );
}

/** The keyword / color / style controls, shared by the add form and each row. */
function KindFields({
  draft,
  onChange,
  idPrefix,
}: {
  draft: KindDraft;
  onChange: (next: KindDraft) => void;
  idPrefix: string;
}) {
  return (
    <div className="kind-fields">
      <label className="kind-field">
        <span>Keyword</span>
        <span className="kind-keyword-input">
          <span className="kind-keyword-slash">/</span>
          <input
            className="text-input"
            value={draft.label}
            maxLength={TEXT_KIND_LABEL_MAX_LENGTH}
            placeholder="shout"
            spellCheck={false}
            autoCapitalize="off"
            onChange={(event) => onChange({ ...draft, label: event.target.value.replace(/\s/g, "") })}
          />
        </span>
      </label>
      <div className="kind-field">
        <span>Color</span>
        <div className="kind-inks" role="group" aria-label="Ink color">
          {INK_PRESETS.map((ink) => (
            <button
              key={ink}
              type="button"
              className={`kind-ink${draft.color.toLowerCase() === ink ? " selected" : ""}`}
              style={{ background: ink }}
              data-tip={ink}
              aria-label={ink}
              onClick={() => onChange({ ...draft, color: ink })}
            />
          ))}
          <label className="kind-ink-custom" data-tip="Custom color">
            <input
              type="color"
              value={isTextKindColor(draft.color) ? draft.color : "#000000"}
              aria-label="Custom ink color"
              onChange={(event) => onChange({ ...draft, color: event.target.value })}
            />
            <span
              className={`kind-ink kind-ink-wheel${
                INK_PRESETS.includes(draft.color.toLowerCase()) ? "" : " selected"
              }`}
              style={
                INK_PRESETS.includes(draft.color.toLowerCase()) ? undefined : { background: draft.color }
              }
            />
          </label>
        </div>
      </div>
      <div className="kind-field">
        <span>Style</span>
        <div className="kind-style-toggles" role="group" aria-label="Text style">
          <button
            type="button"
            className={`kind-toggle${draft.italic ? " on" : ""}`}
            aria-pressed={draft.italic}
            id={`${idPrefix}-italic`}
            onClick={() => onChange({ ...draft, italic: !draft.italic })}
          >
            <em>I</em> Italic
          </button>
          <button
            type="button"
            className={`kind-toggle${draft.bold ? " on" : ""}`}
            aria-pressed={draft.bold}
            id={`${idPrefix}-bold`}
            onClick={() => onChange({ ...draft, bold: !draft.bold })}
          >
            <strong>B</strong> Semi-bold
          </button>
        </div>
      </div>
    </div>
  );
}

function toInput(draft: KindDraft): TextKindInput {
  return {
    label: draft.label.trim(),
    color: draft.color.toLowerCase(),
    italic: draft.italic,
    bold: draft.bold,
  };
}

/** One saved kind: preview, fields, Save (when dirty), Remove. */
function KindRow({ kind }: { kind: TextKind }) {
  const [draft, setDraft] = useState<KindDraft>(() => draftOf(kind));
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const dirty = !sameDraft(draft, draftOf(kind));
  const valid = isTextKindLabel(draft.label) && isTextKindColor(draft.color);

  const save = async () => {
    setSaving(true);
    try {
      await updateTextKind(kind.id, toInput(draft));
      await loadTextKinds();
      showToast(`Updated /${draft.label.trim()}`);
    } catch (error) {
      showToast(kindErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmingDelete(false);
    try {
      await deleteTextKind(kind.id);
      await loadTextKinds();
      showToast(`Removed /${kind.label}`);
    } catch (error) {
      showToast(kindErrorMessage(error));
    }
  };

  return (
    <li className="kind-row">
      <div className="kind-row-head">
        <code className="kind-row-keyword">/{kind.label}</code>
        <KindPreview
          className={dirty ? undefined : textTypeClassName(kind.id)}
          style={dirty ? draftStyle(draft) : undefined}
          brackets={["‹", "›"]}
        />
        <div className="spacer" />
        {dirty && (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(draftOf(kind))}>
              Revert
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!valid || saving}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        )}
        <button
          type="button"
          className="btn btn-ghost kind-remove"
          onClick={() => setConfirmingDelete(true)}
        >
          Remove
        </button>
      </div>
      <KindFields draft={draft} onChange={setDraft} idPrefix={`kind-${kind.id}`} />
      {confirmingDelete && (
        <Modal title={`Remove /${kind.label}?`} onClose={() => setConfirmingDelete(false)}>
          <p>
            Writers lose <code>/{kind.label}</code> from the menu. Text already marked with it
            keeps its words and its ‹ › brackets, but goes back to plain ink.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void remove()}>
              Remove
            </button>
          </div>
        </Modal>
      )}
    </li>
  );
}

const EMPTY_DRAFT: KindDraft = { label: "", color: INK_PRESETS[0]!, italic: false, bold: false };

/** The add form — a live preview above the fields, "Add kind" below. */
function AddKindForm({ atCap }: { atCap: boolean }) {
  const [draft, setDraft] = useState<KindDraft>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const valid = isTextKindLabel(draft.label) && isTextKindColor(draft.color);

  const add = async () => {
    setAdding(true);
    try {
      await createTextKind(toInput(draft));
      await loadTextKinds();
      showToast(`Added /${draft.label.trim()} — it's in the / menu now`);
      setDraft(EMPTY_DRAFT);
    } catch (error) {
      showToast(kindErrorMessage(error));
    } finally {
      setAdding(false);
    }
  };

  return (
    <form
      className="kind-add"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !adding && !atCap) void add();
      }}
    >
      <div className="kind-row-head">
        <code className="kind-row-keyword kind-row-keyword-new">
          /{draft.label.trim() === "" ? "keyword" : draft.label.trim()}
        </code>
        <KindPreview style={draftStyle(draft)} brackets={["‹", "›"]} />
        <div className="spacer" />
        <button type="submit" className="btn btn-primary" disabled={!valid || adding || atCap}>
          {adding ? "Adding…" : "Add kind"}
        </button>
      </div>
      <KindFields draft={draft} onChange={setDraft} idPrefix="kind-new" />
      {atCap && (
        <p className="tx-hint">
          This workspace already has {TEXT_KIND_MAX_COUNT} kinds — remove one to add another.
        </p>
      )}
    </form>
  );
}

export function TextKindsSection() {
  const kinds = useTextKinds();

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>Script line kinds</h2>
        <span className="admin-card-hint">
          What a writer gets when they type <code>/</code> in a cell. Sound and Visual are built
          in. Add your own keyword with a color and style — the text a writer types after it is
          shown that way, in ‹ ›, on every board.
        </span>
      </div>

      <ul className="kind-list">
        <li className="kind-row kind-row-builtin">
          <div className="kind-row-head">
            <code className="kind-row-keyword">/sound</code>
            <KindPreview className="tt-sound" brackets={["[", "]"]} />
            <div className="spacer" />
            <span className="kind-builtin-tag">Built in</span>
          </div>
        </li>
        <li className="kind-row kind-row-builtin">
          <div className="kind-row-head">
            <code className="kind-row-keyword">/visual</code>
            <KindPreview className="tt-visual" brackets={["[", "]"]} />
            <div className="spacer" />
            <span className="kind-builtin-tag">Built in</span>
          </div>
        </li>
        {kinds.map((kind) => (
          <KindRow key={kind.id} kind={kind} />
        ))}
      </ul>

      <AddKindForm atCap={kinds.length >= TEXT_KIND_MAX_COUNT} />
    </section>
  );
}
